import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useStoredFlag } from './useStoredFlag';

export const RAIL_STORAGE_KEY = 'aiai.mc.rail';

/**
 * The hub rail as an on-demand drawer: a small "Live \u00b7 N" button at the scene's top-right
 * opens a right-side panel over the scene (the needs-you list, the live feed, Stop all and
 * Start). Closed by default and remembered per viewer. Needs-you items are never lost while
 * it is closed: the button carries a signal dot and says how many need you.
 */
export function LiveDrawer({
  working, needsYou, children,
}: {
  /** Agents actually working; signal orange only when > 0. */
  working: number;
  /** Needs-you items; > 0 adds the dot and the count to the button's name. */
  needsYou: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useStoredFlag(RAIL_STORAGE_KEY, false);
  const drawerId = `live-drawer${useId().replace(/:/g, '')}`;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  /** Set when the viewer opens it, so a drawer remembered open does not grab focus on page load. */
  const focusOnOpen = useRef(false);

  useEffect(() => {
    if (open && focusOnOpen.current) {
      focusOnOpen.current = false;
      drawerRef.current?.focus();
    }
  }, [open]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  // A click outside closes it, except inside a modal it opened (Stop all's confirm, the
  // launch dialog): those sit in a portal, outside the drawer's DOM.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t || drawerRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, setOpen]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    // Keys from a portalled modal bubble here through React; that modal handles its own Esc.
    if (!drawerRef.current?.contains(e.target as Node)) return;
    e.stopPropagation(); // the peek card's document-level Esc stays put
    close(true);
  };

  const name = `Live \u00b7 ${working} working${needsYou > 0 ? `, ${needsYou} need you` : ''}`;
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={drawerId}
        aria-label={name}
        title={name}
        onClick={() => {
          if (open) return close(false);
          focusOnOpen.current = true;
          setOpen(true);
        }}
        className="btn-secondary btn-sm absolute right-3 top-3 z-20 h-8 bg-surface-1 shadow-sm sm:right-4 sm:top-4"
      >
        {needsYou > 0 && <span data-testid="needs-you-dot" aria-hidden="true" className="h-2 w-2 rounded-full bg-signal" />}
        <span>Live</span>
        <span aria-hidden="true" className="text-ink-faint">{'\u00b7'}</span>
        <span data-testid="live-count" className={`tabular-nums ${working > 0 ? 'text-signal' : 'text-ink-muted'}`}>
          {working}
        </span>
      </button>
      <aside
        ref={drawerRef}
        id={drawerId}
        aria-label="Live feed"
        tabIndex={-1}
        hidden={!open}
        onKeyDown={onKeyDown}
        // `hidden` alone loses to a display utility in the cascade, so the class follows it.
        className={`${open ? 'flex' : 'hidden'} absolute inset-y-0 right-0 z-30 w-[340px] max-w-full flex-col overflow-y-auto border-l border-line bg-surface-1 p-4 shadow-lg outline-none`}
      >
        <button
          type="button"
          aria-label="Close live feed"
          title="Close"
          onClick={() => close(true)}
          className="absolute right-2 top-2 rounded-[5px] p-1 text-ink-muted hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="flex min-h-0 flex-1 flex-col pt-6">{children}</div>
      </aside>
    </>
  );
}
